import { useFormContext, get } from 'react-hook-form';
import { useI18n } from '@react/i18n';
import { CheckoutFormValues, CheckoutState } from '../types';
import { Collapse } from './ui';

interface Props {
    state: CheckoutState;
    links: { back: string; terms: string; privacy: string };
}

/** Souhlasy: povinné VOP, newsletter (jen CZ/SK trh), volitelné vytvoření účtu (+ povinný souhlas s registrací), testovací objednávka pro adminy. */
export default function AgreementsSection({ state, links }: Props) {
    const t = useI18n();
    const { register, watch, formState } = useFormContext<CheckoutFormValues>();
    const createAccount = watch('agreements.createAccount');
    const vopChecked = watch('agreements.vop');
    const newsletterChecked = watch('agreements.newsletter');
    const registrationChecked = watch('agreements.registration');
    const heurekaChecked = watch('agreements.heureka');
    const testingChecked = watch('testing');
    // vizualni fajfku dela trida active-panel na labelu (nativni input ma opacity 0)
    const check = (base: string, checked: boolean) => `${base}${checked ? ' active-panel' : ''}`;
    const vopError = get(formState.errors, 'agreements.vop') as { message?: string } | undefined;
    const registrationError = get(formState.errors, 'agreements.registration') as { message?: string } | undefined;

    return (
        <fieldset>
            <label className={check('b-check b-check--small b-check--large--no-underline', vopChecked)}>
                <span>
                    {t('I agree to the')} <a href={links.terms} target="_blank" rel="noreferrer">{t('Terms and Conditions')}</a>{' '}
                    {t('and the')} <a href={links.privacy} target="_blank" rel="noreferrer">{t('Privacy Policy')}</a>, {t('including how my personal data is used to fulfill the order.')}
                </span>
                <input
                    type="checkbox"
                    {...register('agreements.vop', { validate: (value) => value || t('You must agree to the terms and conditions') })}
                />
            </label>
            {vopError?.message && <p className="msg msg--error rc-msg">{vopError.message}</p>}

            {/* Newsletter souhlas bezi jen na CZ/SK trhu (parita se starymi CZ/SK weby) */}
            {state.market !== 'international' && (
                <label className={check('b-check b-check--small b-check--large--no-underline', newsletterChecked)}>
                    <span>
                        {t('I want to send actions and discounts and I agree with the')}{' '}
                        <a href={links.privacy} target="_blank" rel="noreferrer">{t('processing of personal data for this purpose')}</a>
                    </span>
                    <input type="checkbox" {...register('agreements.newsletter')} />
                </label>
            )}

            {!state.customer && (
                <>
                    <label className={check('b-check b-check--small b-check--large--no-underline b-check--block', createAccount)}>
                        <span>{t('Create an account')}</span>
                        <input type="checkbox" {...register('agreements.createAccount')} />
                    </label>

                    <Collapse open={createAccount}>
                        <>
                            <label className={check('b-check b-check--small b-check--large--no-underline b-check--block', registrationChecked)}>
                                <span>
                                    {t('I agree with')} <a href={links.terms} target="_blank" rel="noreferrer">{t('the terms and conditions')}</a>{' '}
                                    {t('and I take note of the')} <a href={links.privacy} target="_blank" rel="noreferrer">{t('processing of personal data')}</a>{' '}
                                    {t('for the purpose of registering the user')}
                                </span>
                                <input
                                    type="checkbox"
                                    {...register('agreements.registration', {
                                        validate: (value, values) => !values.agreements.createAccount || value || t('You must agree to the registration, otherwise you can not register'),
                                    })}
                                />
                            </label>
                            {registrationError?.message && <p className="msg msg--error rc-msg">{registrationError.message}</p>}
                        </>
                    </Collapse>
                </>
            )}

            {state.adminAuth && (
                <label className={check('b-check b-check--small b-check--large--no-underline b-check--block', testingChecked)}>
                    <span>{t('Testovací objednávka')}</span>
                    <input type="checkbox" {...register('testing')} />
                </label>
            )}

            {/* Heureka Overeno bezi jen na CZ/SK trhu (viz OrderPlacementService::heurekaOvereno) */}
            {state.market !== 'international' && (
                <label className={check('b-check b-check--small b-check--large--no-underline b-check--border', heurekaChecked)}>
                    <span>{t('I do not agree with sending a customer-verified Satisfaction Questionnaire, which helps improve your service.')}</span>
                    <input type="checkbox" {...register('agreements.heureka')} />
                </label>
            )}
        </fieldset>
    );
}
